/**
 * Full address entry, validated against the rules core will actually apply.
 *
 * `validateRestrictedAddress` in `services/core/internal/domain/commerce.go`
 * bounds every field, requires a two-letter region and a 5–10 character postal
 * code, and refuses anything that is not United States. Those rules are
 * reproduced here rather than approximated, so a customer is told what is wrong
 * beside the field instead of receiving an opaque 400 after submitting.
 *
 * Two rules from the packet shape this file:
 *
 * - **The address is never silently patched.** Nothing here corrects a state
 *   code, expands an abbreviation, or "helpfully" reformats a postal code. The
 *   value that goes to core is the value the customer typed, trimmed of
 *   surrounding whitespace and nothing else. Core hashes this address and PayPal
 *   is checked against that hash; quietly rewriting it here would turn an
 *   honest mismatch into a silent substitution.
 * - **It never leaves the page.** No field in this module is persisted, logged,
 *   put in a URL, or sent anywhere but the checkout command body.
 */

export interface AddressDraft {
  readonly recipientName: string;
  readonly organization: string;
  readonly addressLine1: string;
  readonly addressLine2: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
}

export const EMPTY_ADDRESS: AddressDraft = {
  recipientName: "",
  organization: "",
  addressLine1: "",
  addressLine2: "",
  locality: "",
  region: "",
  postalCode: ""
};

export type AddressField = keyof AddressDraft;

/** Field id -> the problem, in the customer's words. Empty means valid. */
export type AddressProblems = Partial<Record<AddressField, string>>;

const CONTROL = /[\u0000-\u001f\u007f]/;

function bounded(value: string, maximum: number): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= maximum && !CONTROL.test(trimmed);
}

export function validateAddress(draft: AddressDraft): AddressProblems {
  const problems: AddressProblems = {};

  if (!bounded(draft.recipientName, 120)) {
    problems.recipientName = "Enter the name the courier should deliver to, up to 120 characters.";
  }
  if (!bounded(draft.addressLine1, 200)) {
    problems.addressLine1 = "Enter the street address, up to 200 characters.";
  }
  if (draft.addressLine2.trim().length > 0 && !bounded(draft.addressLine2, 200)) {
    problems.addressLine2 = "Apartment or suite is optional, and at most 200 characters.";
  }
  if (draft.organization.trim().length > 0 && !bounded(draft.organization, 120)) {
    problems.organization = "Company is optional, and at most 120 characters.";
  }
  if (!bounded(draft.locality, 120)) {
    problems.locality = "Enter the city, up to 120 characters.";
  }
  if (!/^[A-Za-z]{2}$/.test(draft.region.trim())) {
    problems.region = "Enter the two-letter state or territory code, like OR.";
  }
  const postal = draft.postalCode.trim();
  if (postal.length < 5 || postal.length > 10 || CONTROL.test(postal)) {
    problems.postalCode = "Enter the ZIP code, 5 to 10 characters.";
  }

  return problems;
}

export function hasProblems(problems: AddressProblems): boolean {
  return Object.keys(problems).length > 0;
}

/** The exact shape core's `RestrictedAddress` expects. US-only by construction. */
export interface RestrictedAddressCommand {
  readonly recipientName: string;
  readonly organization?: string;
  readonly addressLine1: string;
  readonly addressLine2?: string;
  readonly locality: string;
  readonly region: string;
  readonly postalCode: string;
  readonly countryCode: "US";
}

export function toCommandAddress(draft: AddressDraft): RestrictedAddressCommand {
  const organization = draft.organization.trim();
  const line2 = draft.addressLine2.trim();
  return {
    recipientName: draft.recipientName.trim(),
    ...(organization.length === 0 ? {} : { organization }),
    addressLine1: draft.addressLine1.trim(),
    ...(line2.length === 0 ? {} : { addressLine2: line2 }),
    locality: draft.locality.trim(),
    region: draft.region.trim(),
    postalCode: draft.postalCode.trim(),
    countryCode: "US"
  };
}

/**
 * Mirrors core's `validEmail`: an `@` that is not first, at least two
 * characters and a dot in the domain, no control characters, 254 bytes max.
 * Deliberately not a "clever" pattern — core is the authority and this only
 * exists to say so beside the field.
 */
export function emailProblem(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Enter the email address your receipt should go to.";
  if (trimmed.length > 254 || CONTROL.test(trimmed)) return "That email address is too long to accept.";
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at >= trimmed.length - 3 || !trimmed.slice(at + 1).includes(".")) {
    return "Enter an email address in the form name@example.com.";
  }
  return null;
}
